import * as React from 'react';
import { FormGroup, FormSelect, FormSelectOption, Label } from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { aggregate } from '../../../../shared/riskregister/aggregate';
import { relaxedBase } from '../../../../shared/riskregister/filters';
import { sortRows } from '../../../../shared/riskregister/sorting';
import type { Payload, ViewState } from '../../../../shared/riskregister/types';
import { CategoryChip, ScoreChip } from '../common/Chips';

export const RecentPanel: React.FunctionComponent<{
  payload: Payload;
  state: ViewState;
  onChange: (change: Partial<ViewState>) => void;
}> = ({ payload, state, onChange }) => {
  const days = Number(state.recent);
  const rows = sortRows(
    relaxedBase(payload.rows, state, payload).filter((row) => row.entered_days !== null && row.entered_days <= days),
    'entered',
  );
  const summary = aggregate(rows, payload);
  const estimated = rows.filter((row) => row.entered_est).length;

  return (
    <div className="risk-register__panel-stack">
      <FormGroup label="Added within" fieldId="risk-register-recent">
        <FormSelect
          id="risk-register-recent"
          value={state.recent}
          onChange={(_event, recent) => onChange({ recent: recent as ViewState['recent'] })}
        >
          {['14', '30', '60', '90'].map((option) => (
            <FormSelectOption key={option} value={option} label={`Last ${option} days`} />
          ))}
        </FormSelect>
      </FormGroup>
      <p className="risk-register__panel-summary">
        <strong>{rows.length}</strong> item{rows.length === 1 ? '' : 's'} entered the Risk Register in the last {days}{' '}
        days. The date is when the RiskRegister label was applied, not when the ticket was created.
      </p>
      <div className="risk-register__chip-row" aria-label="Recently added split">
        <Label color="blue" isCompact>
          {summary.open} open
        </Label>
        <Label color="green" isCompact>
          {summary.closed} completed
        </Label>
        <Label color="orange" isCompact>
          {estimated} estimated entry date{estimated === 1 ? '' : 's'}
        </Label>
        <Label color="red" isCompact>
          {summary.tiers.critical} critical
        </Label>
        <Label color="grey" isCompact>
          {summary.tiers.unscored} unscored
        </Label>
      </div>
      {!rows.length ? (
        <p className="risk-register__empty">No items were added during this interval.</p>
      ) : (
        <div className="risk-register__table-wrap">
          <Table aria-label="Recently added Risk Register items" variant="compact">
            <Thead>
              <Tr>
                <Th scope="col">Ticket</Th>
                <Th scope="col">Summary</Th>
                <Th scope="col">Added to register</Th>
                <Th scope="col">Category</Th>
                <Th scope="col">Score</Th>
                <Th scope="col">Status</Th>
                <Th scope="col">Owner</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr key={row.key}>
                  <Td dataLabel="Ticket">
                    <a href={`${payload.jira}${row.key}`} target="_blank" rel="noreferrer">
                      {row.key}
                    </a>
                  </Td>
                  <Td dataLabel="Summary">{row.summary}</Td>
                  <Td dataLabel="Added to register">
                    {row.entered}
                    {row.entered_est ? ' *' : ''}
                  </Td>
                  <Td dataLabel="Category">
                    <CategoryChip row={row} payload={payload} />
                  </Td>
                  <Td dataLabel="Score">
                    <ScoreChip row={row} />
                  </Td>
                  <Td dataLabel="Status">{row.status}</Td>
                  <Td dataLabel="Owner">{row.assignee || 'Unassigned'}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      )}
    </div>
  );
};
